import Observable from "zen-observable"
import type {ZenObservable} from "zen-observable"
import AuthStreamBase from "@irati/auth/streams/auth/base"
import AuthStream from "@irati/auth/streams/auth"
import UnauthedStream from "@irati/auth/streams/unauthed"
import GlobalStream from "@irati/auth/streams/global"
import {Branding} from "@irati/types"

export type OnNext = (branding: Branding) => void

interface AuthServiceWithBranding {
  subscribe: AuthStreamBase<{branding: Branding}>["subscribe"]
}

type Listeners = "authedListener" | "unauthedListener" | "globalListener"

/**
 * @description: We listen to various auth streams where branding can come from.
 * Heirachy of authed (e.g. logged in user), unauthed (e.g. tenant page) then global stylings (general Perkbox stlyings)
 */
class BrandingListener {
  private authedListener: ZenObservable.Subscription | null = null
  private unauthedListener: ZenObservable.Subscription | null = null
  private globalListener: ZenObservable.Subscription | null = null

  private authedBranding: Branding | null = null
  private unauthedBranding: Branding | null = null
  private globalBranding: Branding | null = null

  subscribe(onNext: OnNext) {
    return new Observable<Branding>((observer) => {
      // Due to circular refs, we need to delay this to next JS tick using setTimeout
      setTimeout(() => {
        if (!this.authedListener) {
          this.listenToStream("authedListener", AuthStream, (branding) => {
            this.authedBranding = branding
            this.sendBranding(observer)
          })
        }
        if (!this.unauthedListener) {
          this.listenToStream("unauthedListener", UnauthedStream, (branding) => {
            this.unauthedBranding = branding
            this.sendBranding(observer)
          })
        }
        if (!this.globalListener) {
          this.listenToStream("globalListener", GlobalStream, (branding) => {
            this.globalBranding = branding
            this.sendBranding(observer)
          })
        }
      })

      return () => {}
    }).subscribe(onNext)
  }

  private sendBranding(observer: ZenObservable.SubscriptionObserver<Branding>) {
    const branding = this.get()
    if (branding) {
      observer.next(branding)
    }
  }

  get() {
    return this.authedBranding || this.unauthedBranding || this.globalBranding || null
  }

  private listenToStream(key: Listeners, service: AuthServiceWithBranding, update: (data: Branding | null) => void) {
    this[key] = service.subscribe(
      (auth) => {
        if (auth && auth.branding) {
          update(auth.branding)
        }
      },
      undefined,
      () => {
        update(null)
        this.listenToStream(key, service, update)
      }
    )
  }
}

export default new BrandingListener()
